import type { CSSProperties } from 'react';
import type { KnownIssue } from '../api/useKnownIssues.ts';
import { Ikon } from '../status/Ikon.tsx';
import { kjentFeilOppforing, lesVersjon, skjulesForNyereEnn, tokenVar } from '../status/status.ts';

/** Skjermene lest for mennesker. Tomt felt betyr alle skjermer (DD-30). */
export function skjermTekst(f: KnownIssue): string {
  return f.screens.length ? f.screens.join(', ') : 'alle skjermer';
}

/** Versjonsområdene lest for mennesker. Tomt felt betyr alle versjoner (DD-30). */
export function versjonTekst(f: KnownIssue): string {
  return f.versions.length ? `versjon ${f.versions.map(lesVersjon).join(', ')}` : 'alle versjoner';
}

/**
 * Statusleddet er etiketten fra `knownIssueStatus` i theme.ts (DD-22), med versjonen bak
 * på en rettet feil: «Rettet i 3.2.0». Ordet defineres aldri i en komponent.
 */
export function statusLedd(f: KnownIssue): string {
  const o = kjentFeilOppforing(f.status);
  return f.status === 'fikset' && f.fixedIn ? `${o.etikett} i ${f.fixedIn}` : o.etikett;
}

/**
 * Meta-linjens ledd etter statusleddet: skjermen, versjonsleddet, om det finnes en omvei,
 * og — bare når kortet ikke vises i widgeten — ett synlighetsledd. Skjuleleddet står BARE
 * på rettede feil: backend skjuler aldri en aktiv (DD-30 presisert 2026-09-19).
 */
export function metaLedd(f: KnownIssue): string[] {
  const rettet = f.status === 'fikset';
  const ledd: string[] = [skjermTekst(f)];
  if (rettet) {
    if (f.fixedIn) ledd.push(skjulesForNyereEnn(f.fixedIn));
  } else {
    ledd.push(f.fixedIn ? `rettes i ${f.fixedIn}` : 'versjon ikke satt');
  }
  if (!rettet && f.workaround) ledd.push('omvei finnes');
  if (!f.visibleInWidget) ledd.push(f.visibleToTesters ? 'bare i programmet' : 'ingen ser denne');
  return ledd;
}

/**
 * Raden (R3.3 — kjente feil-konsept v0.1, DD-61, DD-67), samme form som innboksens:
 *
 *   [statusikon i statusens farge] [tittel, body, halvfet]        [ETT tall, body]
 *                                  [én meta-linje i small, ledd skilt med «·»]
 *
 * Tre elementer utenom meta-linjen. Tallet er «N har spurt» i BEGGE statuser — reach er
 * levende også etter varselet, og kontrakten har verken varslet-antall eller rettet-dato.
 * INGEN bryter og INGEN knapp i raden: de bor i detaljruten (F8 — raden hadde elleve
 * elementer, to brytere og en «Rettet»-knapp).
 */
export function KjentFeilRad({ f, valgt, onVelg }: { f: KnownIssue; valgt: boolean; onVelg: () => void }) {
  const o = kjentFeilOppforing(f.status);
  const ledd = metaLedd(f);
  const farge = { '--kf-status-fg': tokenVar(o.fg) } as CSSProperties;
  const etikett = `Kjent feil: ${f.title}. ${statusLedd(f)}, ${f.reach} har spurt, ${ledd.join(', ')}.`;

  return (
    <li>
      <button type="button" className={`panel-rad${valgt ? ' panel-rad-valgt' : ''}`} aria-label={etikett} aria-current={valgt ? 'true' : undefined} onClick={onVelg} data-testid={`kf-rad-${f.id}`}>
        <span className="panel-rad-sporikon panel-kf-ikon" style={farge} data-status={f.status}>
          <Ikon navn={o.ikon} />
        </span>
        <span className="panel-rad-tekst">{f.title}</span>
        <span className="panel-rad-tall" data-testid="kf-tall">
          <b>{f.reach}</b> har spurt
        </span>
        <span className="panel-rad-meta" data-testid="kf-meta">
          <span className="panel-rad-ledd panel-kf-statusledd" style={farge}>
            {statusLedd(f)}
          </span>
          {ledd.map((m) => (
            <span key={m} className="panel-rad-ledd">
              {m}
            </span>
          ))}
        </span>
      </button>
    </li>
  );
}
